import { useQuery } from '@tanstack/react-query';
import { Activity, Database, Server } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Card, CardLabel } from '@/components/ui/card';
import { StatCard } from '@/components/StatCard';
import { DataTable, type Column } from '@/components/DataTable';

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

interface MongoHealthResponse {
  ok: boolean;
  latencyMs: number;
  collections: Record<string, number>;
}

interface CacheItem {
  key: string;
  computedAt: string;
  ageMs: number;
  ttlRemainingMs: number;
  fresh: boolean;
}
interface CacheResponse {
  total: number;
  fresh: number;
  items: CacheItem[];
}

interface BugReportRow {
  _id: string;
  message: string;
  createdAt: string;
  user: { uid: string | null; email: string | null; name: string | null } | null;
}
interface BugReportsResponse {
  limit: number;
  total: number;
  items: BugReportRow[];
}

function fmtMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}min`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const cacheColumns: Column<CacheItem>[] = [
  {
    key: 'key',
    header: 'Cache key',
    cell: (r) => <code className="text-xs text-fg-muted">{r.key}</code>,
  },
  {
    key: 'fresh',
    header: 'Status',
    width: '80px',
    cell: (r) => (
      <span
        className={cn(
          'inline-block rounded-md px-2 py-0.5 text-xs font-medium',
          r.fresh
            ? 'bg-state-success/10 text-brand-cyan'
            : 'bg-surface-divider/30 text-fg-subtle',
        )}
      >
        {r.fresh ? 'Fresh' : 'Stale'}
      </span>
    ),
  },
  {
    key: 'ageMs',
    header: 'Age',
    align: 'right',
    width: '80px',
    cell: (r) => <span className="text-fg-muted">{fmtMs(r.ageMs)}</span>,
  },
  {
    key: 'ttlRemainingMs',
    header: 'TTL left',
    align: 'right',
    width: '90px',
    cell: (r) => (
      <span className={cn(r.fresh ? 'text-fg' : 'text-fg-subtle')}>
        {fmtMs(r.ttlRemainingMs)}
      </span>
    ),
  },
  {
    key: 'computedAt',
    header: 'Computed',
    align: 'right',
    width: '140px',
    cell: (r) => <span className="text-fg-muted">{fmtDate(r.computedAt)}</span>,
  },
];

const bugReportColumns: Column<BugReportRow>[] = [
  {
    key: 'user',
    header: 'Reporter',
    width: '180px',
    cell: (r) =>
      r.user ? (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.user.name ?? '—'}</div>
          <div className="text-xs text-fg-subtle truncate">
            {r.user.email ?? '(no email)'}
          </div>
        </div>
      ) : (
        <span className="text-fg-subtle">(unknown user)</span>
      ),
  },
  {
    key: 'message',
    header: 'Message',
    cell: (r) => (
      <span className="text-fg-muted whitespace-pre-wrap break-words">
        {r.message}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Filed',
    align: 'right',
    width: '120px',
    cell: (r) => fmtDate(r.createdAt),
  },
];

export function Health(): JSX.Element {
  const errMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  const mongo = useQuery({
    queryKey: ['metrics', 'health', 'mongo'],
    queryFn: api<MongoHealthResponse>('/api/metrics/health/mongo'),
    refetchInterval: 15_000,
  });
  const cacheState = useQuery({
    queryKey: ['metrics', 'health', 'cache'],
    queryFn: api<CacheResponse>('/api/metrics/health/cache'),
    refetchInterval: 5_000,
  });
  const bugReports = useQuery({
    queryKey: ['metrics', 'health', 'bug-reports', 20],
    queryFn: api<BugReportsResponse>(
      '/api/metrics/health/bug-reports?limit=20',
    ),
  });

  const totalDocs = mongo.data
    ? Object.values(mongo.data.collections).reduce((s, n) => s + Math.max(0, n), 0)
    : undefined;

  const collectionRows = mongo.data
    ? Object.entries(mongo.data.collections)
        .map(([name, n]) => ({ name, n }))
        .sort((a, b) => b.n - a.n)
    : undefined;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Health</h1>
        <p className="text-sm text-fg-muted">
          Backend status — Mongo connectivity, the in-process metric cache,
          and recent user bug reports. Mongo refreshes every 15s; cache state
          refreshes every 5s.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Mongo latency"
          value={mongo.data ? `${mongo.data.latencyMs} ms` : undefined}
          loading={mongo.isLoading}
          error={errMsg(mongo)}
          hint={mongo.data?.ok ? 'connected' : 'check connection'}
        />
        <StatCard
          label="Total docs (estimated)"
          value={totalDocs}
          loading={mongo.isLoading}
          error={errMsg(mongo)}
          hint="Sum across all collections"
        />
        <StatCard
          label="Cache keys (fresh / total)"
          value={
            cacheState.data
              ? `${cacheState.data.fresh} / ${cacheState.data.total}`
              : undefined
          }
          loading={cacheState.isLoading}
          error={errMsg(cacheState)}
          hint="In-process TTL cache"
        />
        <StatCard
          label="Bug reports"
          value={bugReports.data?.total}
          loading={bugReports.isLoading}
          error={errMsg(bugReports)}
          hint="All-time"
        />
      </div>

      {/* Mongo + collection counts */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Database size={16} className="text-brand-cyan" />
          <div>
            <CardLabel>MongoDB</CardLabel>
            <div className="text-lg font-bold mt-1">
              {mongo.data?.ok ? (
                <span>
                  Connected ·{' '}
                  <span className="text-brand-cyan">
                    {mongo.data.latencyMs}ms
                  </span>{' '}
                  ping
                </span>
              ) : mongo.isLoading ? (
                'Checking…'
              ) : (
                <span className="text-state-danger">Down</span>
              )}
            </div>
          </div>
        </div>
        {mongo.error ? (
          <div className="text-sm text-state-danger">{errMsg(mongo)}</div>
        ) : (
          <DataTable
            columns={[
              {
                key: 'name',
                header: 'Collection',
                cell: (r) => <span className="font-medium">{r.name}</span>,
              },
              {
                key: 'n',
                header: 'Docs (estimated)',
                align: 'right',
                width: '160px',
                cell: (r) => (
                  <span className="tabular-nums">
                    {r.n < 0 ? '—' : r.n.toLocaleString()}
                  </span>
                ),
              },
            ]}
            rows={collectionRows}
            loading={mongo.isLoading}
            rowKey={(r) => r.name}
          />
        )}
      </Card>

      {/* Cache state */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-brand-cyan" />
          <div>
            <CardLabel>Metric cache</CardLabel>
            <div className="text-lg font-bold mt-1">
              In-process TTL cache — what each metric is reading from
            </div>
          </div>
        </div>
        <DataTable
          columns={cacheColumns}
          rows={cacheState.data?.items}
          loading={cacheState.isLoading}
          error={errMsg(cacheState)}
          empty="Cache is empty. The first request to any metric will populate it."
          rowKey={(r) => r.key}
        />
      </Card>

      {/* Bug reports */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Server size={16} className="text-state-warning" />
          <div>
            <CardLabel>Recent bug reports</CardLabel>
            <div className="text-lg font-bold mt-1">
              From the Malsi app · most recent {bugReports.data?.limit ?? 20}
            </div>
          </div>
        </div>
        <DataTable
          columns={bugReportColumns}
          rows={bugReports.data?.items}
          loading={bugReports.isLoading}
          error={errMsg(bugReports)}
          empty="No bug reports."
          rowKey={(r) => r._id}
        />
      </Card>
    </div>
  );
}
